'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useState } from 'react';
import { CTA } from '@/components/CTA/CTA';
import { components } from '@/types/strapi';

type StripImageCTA = components['schemas']['DynamicZoneStripImageCtaComponent'];

interface StripImageCTA_ extends StripImageCTA {
	isFirst: boolean;
}
export default function StripImageCTA({
	heading = 'Reach More & Spend Less With Our Bulk Messaging Services',
	subheading = '',
	ctaButtons,
	image,
	isFirst = true,
	backgroundColor = 'bg-[#4338CA]',
}: StripImageCTA_) {
	const [isHovered, setIsHovered] = useState(false);

	const imageVariants = {
		hover: {
			scale: 1.05,
			transition: { duration: 0.3 },
		},
		tap: {
			scale: 0.95,
		},
	};

	const textVariants = {
		hover: {
			y: -5,
			transition: { duration: 0.3 },
		},
	};

	// Process the ctaButtons to ensure it's an array
	const buttons = Array.isArray(ctaButtons)
		? ctaButtons
		: ctaButtons
			? [ctaButtons]
			: [];

	return (
		<section
			className={`strip-image-cta-section relative mx-auto flex items-center overflow-hidden py-8 sm:py-8 lg:px-8 ${backgroundColor}`}>
			<noscript>
				<style>{`
					.strip-image-cta-section [style*='opacity'],
					.strip-image-cta-section [style*='translateX'],
					.strip-image-cta-section [style*='translateY'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl px-6 lg:px-8">
				<div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 md:grid-cols-2 lg:mx-0 lg:max-w-screen-lg lg:items-center">
					<motion.div
						initial={{ opacity: 0, x: -20 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5 }}
						onHoverStart={() => setIsHovered(true)}
						onHoverEnd={() => setIsHovered(false)}
						animate={isHovered ? 'hover' : 'initial'}
						variants={textVariants}>
						{isFirst && (
							<motion.h1
								className="text-3xl font-bold tracking-tight text-white sm:text-4xl"
								animate={{ scale: isHovered ? 1.02 : 1 }}
								transition={{ duration: 0.2 }}>
								{heading}
							</motion.h1>
						)}
						{!isFirst && (
							<motion.h2
								className="text-3xl font-bold tracking-tight text-white sm:text-4xl"
								animate={{ scale: isHovered ? 1.02 : 1 }}
								transition={{ duration: 0.2 }}>
								{heading}
							</motion.h2>
						)}
						<AnimatePresence>
							{subheading && (
								<motion.p
									className="mt-4 text-lg text-gray-200"
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -10 }}
									transition={{ duration: 0.3 }}>
									{subheading}
								</motion.p>
							)}
						</AnimatePresence>
						<div className="mt-8">
							{buttons.length > 0 && <CTA CTAs={buttons} />}
						</div>
					</motion.div>
					<motion.div
						className="relative cursor-pointer"
						initial={{ opacity: 0, x: 20 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5 }}
						whileHover="hover"
						whileTap="tap"
						variants={imageVariants}>
						{image && image.url && (
							<Image
								src={image.url}
								alt={image.alternativeText || 'CTA Image'}
								width={400}
								height={400}
								className="aspect-video object-contain transition-transform duration-300"
							/>
						)}
						<motion.div
							className="absolute inset-0 bg-indigo-600 opacity-0"
							whileHover={{ opacity: 0.1 }}
							transition={{ duration: 0.3 }}
						/>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
